import type {
  AuthenticationResult,
  BrowserAuthOptions,
  IPublicClientApplication,
} from '@azure/msal-browser';
import { logger } from './logger';
import { getEntraClientId } from './runtimeConfig';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL ?? '';
const ENTRA_AUTHORITY =
  (import.meta.env.VITE_ENTRA_AUTHORITY as string | undefined) ??
  'https://login.microsoftonline.com/common';

let msalInstance: IPublicClientApplication | null = null;

export function isMicrosoftSignInConfigured(): boolean {
  return Boolean(getEntraClientId());
}

export function isNestedAppAuthSupported(): boolean {
  if (typeof Office === 'undefined') return false;
  return Office.context?.requirements?.isSetSupported('NestedAppAuth', '1.1') === true;
}

export function getMailboxEmail(): string | undefined {
  if (typeof Office === 'undefined') return undefined;
  return Office.context?.mailbox?.userProfile?.emailAddress?.trim().toLowerCase() || undefined;
}

export function mailboxMatchesEmail(email: string | null | undefined): boolean {
  const mailboxEmail = getMailboxEmail();
  return Boolean(mailboxEmail && email && mailboxEmail === email.trim().toLowerCase());
}

async function getLoginHint(): Promise<string | undefined> {
  const mailboxEmail = getMailboxEmail();
  if (mailboxEmail) return mailboxEmail;

  try {
    const officeAuth = (
      Office as unknown as {
        auth?: { getAuthContext?: () => Promise<{ userPrincipalName?: string }> };
      }
    ).auth;
    const authContext = await officeAuth?.getAuthContext?.();
    return authContext?.userPrincipalName;
  } catch (err) {
    logger.warn('[microsoft-auth] could not read Office login hint', { err });
    return undefined;
  }
}

async function getMsalInstance(): Promise<IPublicClientApplication> {
  const entraClientId = getEntraClientId();
  if (!entraClientId) {
    throw new Error('Microsoft sign-in is not configured for this environment.');
  }
  if (!isNestedAppAuthSupported()) {
    throw new Error('This Outlook host does not support Microsoft single sign-on yet.');
  }
  if (!msalInstance) {
    const { createNestablePublicClientApplication } = await import('@azure/msal-browser');
    msalInstance = await createNestablePublicClientApplication({
      auth: {
        clientId: entraClientId,
        authority: ENTRA_AUTHORITY,
        supportsNestedAppAuth: true,
      } as BrowserAuthOptions & { supportsNestedAppAuth: true },
      cache: {
        cacheLocation: 'localStorage',
      },
    });
  }
  return msalInstance;
}

export async function exchangeMicrosoftIdentityForCustomToken(opts?: {
  allowInteractive?: boolean;
}): Promise<{
  customToken: string;
  email: string;
}> {
  const { InteractionRequiredAuthError } = await import('@azure/msal-browser');
  const msal = await getMsalInstance();
  const mailboxEmail = getMailboxEmail();
  if (!mailboxEmail) {
    throw new Error('Outlook did not expose the active mailbox identity. Close and reopen AIRA.');
  }
  const request = {
    scopes: ['User.Read'],
    loginHint: await getLoginHint(),
  };

  let result: AuthenticationResult;
  try {
    result = await msal.ssoSilent(request);
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError && opts?.allowInteractive !== false) {
      result = await msal.acquireTokenPopup(request);
    } else {
      throw err;
    }
  }

  if (!result.idToken) {
    throw new Error('Microsoft sign-in did not return an ID token.');
  }

  const response = await fetch(`${BACKEND_URL}/v1/auth/microsoft-exchange`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      id_token: result.idToken,
      mailbox_email: mailboxEmail,
    }),
    signal: AbortSignal.timeout(12_000),
  });

  const body = (await response.json().catch(() => ({}))) as {
    custom_token?: string;
    email?: string;
    message?: string;
    error?: string;
  };

  if (!response.ok || !body.custom_token || !body.email) {
    throw new Error(
      body.message ?? body.error ?? `Microsoft sign-in failed: HTTP ${response.status}`,
    );
  }

  return { customToken: body.custom_token, email: body.email };
}
